import "../styles/pages/Contact.css";
import { useLang } from "../context/LangContext";
import { useEffect, useMemo, useState } from "react";
import { envOk, getEnvDebug, sendContactMail } from "../lib/email";

function Toast({ open, type, msg, onClose }) {
  if (!open) return null;

  return (
    <div className={`toast ${type}`} role="alert" onClick={onClose}>
      <span className="toast-dot" aria-hidden="true" />
      <span>{msg}</span>
    </div>
  );
}

function StatusIcon({ type }) {
  if (type === "address") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 21s-6-5.2-6-11a6 6 0 1 1 12 0c0 5.8-6 11-6 11Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="10"
          r="2.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (type === "email") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 6h16v12H4z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="m5 7 7 6 7-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 6v6l4 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export default function Contact() {
  const { dict } = useLang();
  const t = dict?.contact;

  const [values, setValues] = useState({
    name: "",
    email: "",
    message: "",
    botfield: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    message: false,
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [toast, setToast] = useState({ open: false, type: "info", msg: "" });

  const hasEnv = envOk();

  useEffect(() => {
    if (!t || hasEnv) return;

    console.error(
      "[Contact] .env anahtarları eksik veya okunamadı.",
      getEnvDebug()
    );

    setStatus({
      state: "error",
      msg: t.envMissing,
    });

    setToast({
      open: true,
      type: "error",
      msg: t.envMissingShort,
    });
  }, [hasEnv, t]);

  useEffect(() => {
    if (!toast.open) return;

    const timer = setTimeout(() => {
      setToast((prev) => ({ ...prev, open: false }));
    }, 4500);

    return () => clearTimeout(timer);
  }, [toast.open]);

  const validate = (v) => {
    const e = {};

    if (!v.name.trim()) e.name = t.nameRequired;

    if (!v.email.trim()) {
      e.email = t.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) {
      e.email = t.emailInvalid;
    }

    if (!v.message.trim()) e.message = t.messageRequired;

    return e;
  };

  const handleChange = (e) => {
    const next = {
      ...values,
      [e.target.name]: e.target.value,
    };

    setValues(next);
    setErrors(validate(next));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({
      ...prev,
      [e.target.name]: true,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(values);
    setErrors(validationErrors);
    setTouched({
      name: true,
      email: true,
      message: true,
    });

    if (Object.keys(validationErrors).length > 0 || values.botfield) return;

    if (!hasEnv) {
      setStatus({
        state: "error",
        msg: t.configMissing,
      });

      setToast({
        open: true,
        type: "error",
        msg: t.configMissingShort,
      });

      return;
    }

    try {
      setStatus({ state: "loading", msg: "" });

      await sendContactMail({
        ...values,
        title: t.mailTitle,
      });

      setStatus({
        state: "success",
        msg: t.sentOk,
      });

      setToast({
        open: true,
        type: "success",
        msg: t.sentOk,
      });

      setValues({
        name: "",
        email: "",
        message: "",
        botfield: "",
      });

      setTouched({
        name: false,
        email: false,
        message: false,
      });

      setErrors({});
    } catch (err) {
      console.error("[Contact] EmailJS error:", err);

      const detail = err?.text || err?.message || String(err || "");
      let human = t.sentFail;

      if (/user ID|public key|unauthorized|401|Permission|origin/i.test(detail)) {
        human = t.authError;
      } else if (/service|template/i.test(detail)) {
        human = t.serviceError;
      } else if (/Network|Failed to fetch|CORS/i.test(detail)) {
        human = t.networkError;
      }

      setStatus({
        state: "error",
        msg: detail ? `${human} (${t.detailLabel}: ${detail})` : human,
      });

      setToast({
        open: true,
        type: "error",
        msg: human,
      });
    }
  };

  const infoItems = useMemo(
    () => [
      {
        key: "address",
        label: t?.addressLabel,
        value: t?.cityLine,
        type: "address",
      },
      {
        key: "email",
        label: t?.emailLabel,
        value: t?.emailValue,
        type: "email",
      },
      {
        key: "hours",
        label: t?.hoursLabel,
        value: t?.hoursValue,
        type: "hours",
      },
    ],
    [t]
  );

  if (!t) return null;

  return (
    <section className="contact-container page-section" id="contact">
      <div className="contact-bg-orb contact-bg-orb-1" aria-hidden="true" />
      <div className="contact-bg-orb contact-bg-orb-2" aria-hidden="true" />
      <div className="contact-grid-noise" aria-hidden="true" />

      <div className="contact-section">
        <div className="contact-head">
          <h2>{t.h2}</h2>
          <p className="contact-sub">{t.sub}</p>
        </div>

        <div className="contact-grid">
          <form className="contact-form" onSubmit={onSubmit} noValidate>
            <div className="contact-card-glow" aria-hidden="true" />

            <label htmlFor="botfield" className="sr-only">
              {t.botFieldLabel}
            </label>

            <input
              id="botfield"
              name="botfield"
              type="text"
              tabIndex="-1"
              className="hp-field"
              value={values.botfield}
              onChange={handleChange}
              autoComplete="off"
            />

            <div className="field-wrap">
              <input
                id="name"
                name="name"
                type="text"
                placeholder={t.name}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={Boolean(touched.name && errors.name)}
                autoComplete="name"
                required
              />
              <span className="field-line" aria-hidden="true" />
            </div>
            {touched.name && errors.name && (
              <span className="field-error">{errors.name}</span>
            )}

            <div className="field-wrap">
              <input
                id="email"
                name="email"
                type="email"
                placeholder={t.email}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={Boolean(touched.email && errors.email)}
                autoComplete="email"
                required
              />
              <span className="field-line" aria-hidden="true" />
            </div>
            {touched.email && errors.email && (
              <span className="field-error">{errors.email}</span>
            )}

            <div className="field-wrap">
              <textarea
                id="message"
                name="message"
                rows="5"
                placeholder={t.message}
                value={values.message}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={Boolean(touched.message && errors.message)}
                required
              />
              <span className="field-line" aria-hidden="true" />
            </div>
            {touched.message && errors.message && (
              <span className="field-error">{errors.message}</span>
            )}

            <button type="submit" disabled={status.state === "loading"}>
              <span className="btn-shine" aria-hidden="true" />
              <span className="btn-inner">
                {status.state === "loading" && (
                  <span className="btn-spinner" aria-hidden="true" />
                )}
                <span>{status.state === "loading" ? t.sending : t.send}</span>
              </span>
            </button>

            <span
              className={`contact-note ${status.state}`}
              role="status"
              aria-live="polite"
            >
              {status.msg}
            </span>
          </form>

          <div className="contact-info">
            <div className="contact-card-glow" aria-hidden="true" />

            <div className="contact-info-top">
              <h3>{t.infoTitle}</h3>
              <p className="contact-info-text">
                {t.infoText ??
                  "Sorularınız, iş birlikleri ve proje talepleriniz için bizimle iletişime geçebilirsiniz."}
              </p>
            </div>

            <div className="contact-info-list">
              {infoItems.map((item) => (
                <div className="contact-info-item" key={item.key}>
                  <div className="contact-info-icon">
                    <StatusIcon type={item.type} />
                  </div>

                  <div className="contact-info-body">
                    <span className="contact-info-label">{item.label}</span>

                    {item.type === "email" ? (
                      <a className="contact-link" href={`mailto:${item.value}`}>
                        {item.value}
                      </a>
                    ) : (
                      <p>{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Toast
        open={toast.open}
        type={toast.type}
        msg={toast.msg}
        onClose={() => setToast({ open: false, type: "info", msg: "" })}
      />
    </section>
  );
}